import { Controller } from 'stimulus'

export default class AnnotationEnhancedController extends Controller {
  static targets = ['icon', 'popup']
  static values = {
    id: String,
    target: String,
    position: Object,
    visibility: String
  }
  
  connect() {
    // Set initial position
    this.updatePosition()
    
    // Set initial visibility
    if (this.visibilityValue === 'visible') {
      this.show()
    }
    
    // Register this annotation with the manager if it exists
    if (window.annotationManager) {
      window.annotationManager.register(this)
    }
    
    // Set up event handlers for resize and scroll
    this.boundUpdatePosition = this.updatePosition.bind(this)
    window.addEventListener('resize', this.boundUpdatePosition)
    window.addEventListener('scroll', this.boundUpdatePosition, { passive: true })
    
    // Handle click outside to close
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.boundHandleClickOutside)
    
    // If we have a target, observe it for changes
    if (this.hasTargetValue && this.targetValue) {
      this.observeTargetChanges()
    }
  }
  
  disconnect() {
    // Clean up event listeners
    window.removeEventListener('resize', this.boundUpdatePosition)
    window.removeEventListener('scroll', this.boundUpdatePosition)
    document.removeEventListener('click', this.boundHandleClickOutside)
    
    // Clean up mutation observer
    if (this.targetObserver) {
      this.targetObserver.disconnect()
    }
    
    // Unregister from manager
    if (window.annotationManager) {
      window.annotationManager.unregister(this)
    }
  }
  
  getPositioningContextOffset() {
    // Find the first positioned parent of the annotation
    let parent = this.element.offsetParent
    
    // If parent is body or there's no positioned parent, no offset needed
    if (!parent || parent === document.body) return { x: 0, y: 0 }
    
    // Get the offset of this positioning context relative to the document
    const rect = parent.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
    
    return {
      x: rect.left + scrollLeft,
      y: rect.top + scrollTop
    }
  }
  
  updatePosition() {
    // Get positioning context offset
    const contextOffset = this.getPositioningContextOffset()
    
    if (this.hasTargetValue && this.targetValue) {
      // Target-based positioning
      const targetElement = document.querySelector(this.targetValue)
      
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect()
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
        
        // Calculate position relative to target center
        const centerX = rect.left + scrollLeft + rect.width / 2
        const centerY = rect.top + scrollTop + rect.height / 2
        
        // Apply offset from position value and adjust for positioning context
        const finalX = centerX + (this.positionValue.x || 0) - contextOffset.x
        const finalY = centerY + (this.positionValue.y || 0) - contextOffset.y
        
        this.element.style.left = `${finalX}px`
        this.element.style.top = `${finalY}px`
        
        // Show the annotation if target is found
        this.element.style.display = ''
      } else {
        // Hide annotation if target not found
        this.element.style.display = 'none'
      }
    } else {
      // Absolute positioning
      if (this.positionValue) {
        this.element.style.left = `${this.positionValue.x - contextOffset.x}px`
        this.element.style.top = `${this.positionValue.y - contextOffset.y}px`
      }
    }
  }
  
  handleClick(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.element.classList.contains('annotation--active')) {
      this.close()
    } else {
      this.open()
    }
  }
  
  handleClickOutside(event) {
    // Don't close if clicking inside the annotation
    if (this.element.contains(event.target)) {
      return
    }
    
    // Don't close if it has the do-not-close class
    if (this.element.classList.contains('annotation--do-not-close')) {
      return
    }
    
    // Close if active
    if (this.element.classList.contains('annotation--active')) {
      this.close()
    }
  }
  
  open() {
    // Close other annotations first (optional behavior)
    if (window.annotationManager) {
      window.annotationManager.closeAll(this)
    }
    
    // Add active classes
    this.element.classList.add('annotation--active', 'annotation--was-active')
    
    // Add opening animation class
    if (this.hasPopupTarget) {
      this.popupTarget.classList.add('annotation__popup--opening')
      this.popupTarget.classList.remove('annotation__popup--closing')
      
      // Remove animation class after animation completes
      setTimeout(() => {
        if (this.hasPopupTarget) {
          this.popupTarget.classList.remove('annotation__popup--opening')
        }
      }, 300)
    }
    
    // Dispatch custom event
    const event = new CustomEvent('annotation:opened', { 
      detail: { id: this.idValue },
      bubbles: true 
    })
    this.element.dispatchEvent(event)
  }
  
  close() {
    // Add closing animation class
    if (this.hasPopupTarget) {
      this.popupTarget.classList.add('annotation__popup--closing')
      this.popupTarget.classList.remove('annotation__popup--opening')
      
      // Remove active class after animation
      setTimeout(() => {
        this.element.classList.remove('annotation--active')
        if (this.hasPopupTarget) {
          this.popupTarget.classList.remove('annotation__popup--closing')
        }
      }, 200)
    } else {
      this.element.classList.remove('annotation--active')
    }
    
    // Dispatch custom event
    const event = new CustomEvent('annotation:closed', { 
      detail: { id: this.idValue },
      bubbles: true 
    })
    this.element.dispatchEvent(event)
  }
  
  show() {
    this.element.classList.remove('annotation--hidden')
    this.visibilityValue = 'visible'
  }
  
  hide() {
    this.element.classList.add('annotation--hidden')
    this.visibilityValue = 'hidden'
  }
  
  reveal(options = {}) {
    // Update position before revealing to ensure correct placement
    this.updatePosition()
    
    // Add revealing animation
    this.element.classList.add('annotation--revealing')
    this.show()
    
    // Remove animation class after completion
    setTimeout(() => {
      this.element.classList.remove('annotation--revealing')
      
      // Optionally open after revealing
      if (options.open) {
        this.open()
      }
    }, 400)
  }
  
  // API for cursor integration
  getCursorTarget() {
    return this.hasIconTarget ? this.iconTarget : this.element
  }
  
  isVisible() {
    return this.visibilityValue === 'visible' && !this.element.classList.contains('annotation--hidden')
  }
  
  isOpen() {
    return this.element.classList.contains('annotation--active')
  }
  
  observeTargetChanges() {
    const targetElement = document.querySelector(this.targetValue)
    if (!targetElement) return
    
    // Create a MutationObserver to watch for changes to the target element
    this.targetObserver = new MutationObserver((mutations) => {
      // Debounce updates to avoid excessive recalculation
      if (this.updateTimeout) clearTimeout(this.updateTimeout)
      
      this.updateTimeout = setTimeout(() => {
        this.updatePosition()
      }, 10) // Small delay to ensure DOM is settled
    })
    
    // Observe the target element and its subtree for any changes
    this.targetObserver.observe(targetElement, {
      attributes: true,
      attributeOldValue: true,
      characterData: true,
      childList: true,
      subtree: true
    })
  }
}